import { createContext, useCallback, useMemo, useState, type ReactNode } from 'react';
import { useTranslations } from 'use-intl';

import { useCreateBugReport } from '../../lib/mutations/bug-reports';
import { errorMessage } from '../../lib/error-message';
import { ReportBugModal } from './ReportBugModal';

// Owns the one Send feedback dialog for the authed tree. The top-bar feedback
// button opens it, and so does any in-product entry point that routes a member
// to feedback (the notice on announcement threads, where replies aren't read),
// so every entry submits through the same modal and mutation.

export interface FeedbackContextValue {
  readonly openFeedback: () => void;
}

export const FeedbackContext = createContext<FeedbackContextValue | null>(null);

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const t = useTranslations('Feedback');
  const [open, setOpen] = useState(false);
  const mutation = useCreateBugReport();

  const openFeedback = useCallback(() => setOpen(true), []);
  const handleSubmit = useCallback(
    async (input: { description: string }) => {
      try {
        await mutation.mutateAsync({ description: input.description });
        return { ok: true as const };
      } catch (e) {
        return { ok: false as const, message: errorMessage(e, t('reportBug.sendError')) };
      }
    },
    [mutation, t],
  );

  const value = useMemo<FeedbackContextValue>(() => ({ openFeedback }), [openFeedback]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <ReportBugModal open={open} onClose={() => setOpen(false)} onSubmit={handleSubmit} />
    </FeedbackContext.Provider>
  );
}
